import { cn } from "../../lib/utils";

/**
 * Card uniforme per il modulo Documenti.
 * Altezza fissa, footer ancorato in basso, titolo/descrizione max 2 righe.
 */
export default function DocumentCard({
  icon: Icon,
  iconWrap = "bg-slate-50 text-slate-600 ring-slate-100",
  title,
  description,
  badge,
  meta,
  metaRight,
  footerLeft,
  footerRight,
  primaryAction,
  secondaryAction,
  menu,
  onClick,
  className,
  testId,
  children,
}) {
  // Niente <button> come contenitore: dentro può esserci il menu (altro pulsante) e
  // <button> annidati sono HTML non valido. Div accessibile con tastiera al suo posto.
  const Wrapper = "div";
  const wrapperProps = onClick
    ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(e);
          }
        },
        className: "w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-inset",
      }
    : { className: "w-full" };
  // Il footer (bordo + meta + azioni) è sempre ancorato in fondo alla card a
  // altezza fissa. Se non ci sono footerLeft/footerRight ma sono presenti
  // delle azioni, mostriamo comunque una riga di footer "vuota" a sinistra
  // così le azioni restano ancorate in basso in modo identico su tutte le card.
  const hasActions = Boolean(primaryAction || secondaryAction || footerRight);
  const hasFooterRow = Boolean(footerLeft || footerRight || hasActions);

  return (
    <div
      className={cn(
        "flex h-[240px] flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm transition hover:border-slate-300 hover:shadow-md",
        onClick && "cursor-pointer",
        className
      )}
      data-testid={testId}
    >
      <Wrapper {...wrapperProps}>
        <div className="flex min-h-0 flex-1 flex-col p-4">
          <div className="mb-3 flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-start gap-2.5">
              {Icon && (
                <div
                  className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-sm ring-1",
                    iconWrap
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-slate-900">
                  {title}
                </h3>
                {badge && <div className="mt-1.5">{badge}</div>}
              </div>
            </div>
            {menu && <div className="shrink-0" onClick={(e) => e.stopPropagation()}>{menu}</div>}
          </div>

          {description && (
            <p className="mb-2 line-clamp-2 text-xs leading-relaxed text-slate-500">
              {description}
            </p>
          )}

          {children}

          {(meta || metaRight) && (
            <div className="mt-auto flex items-center justify-between gap-2 text-[11px] text-slate-400">
              <span className="min-w-0 truncate">{meta}</span>
              {metaRight && <span className="shrink-0">{metaRight}</span>}
            </div>
          )}
        </div>
      </Wrapper>

      {hasFooterRow && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-100 px-4 py-2.5">
          <span className="min-w-0 truncate text-[11px] text-slate-400">{footerLeft}</span>
          <div className="flex shrink-0 items-center gap-1.5">
            {primaryAction}
            {secondaryAction}
            {footerRight}
          </div>
        </div>
      )}
    </div>
  );
}

/** Card orizzontale per Moduli da firmare (lista estesa) */
export function DocumentCardHorizontal({
  icon: Icon,
  iconWrap = "bg-slate-50 text-slate-600 ring-slate-100",
  title,
  description,
  badge,
  meta,
  stats,
  actions,
  menu,
  className,
  testId,
}) {
  return (
    <div
      className={cn(
        "flex min-h-[140px] flex-col rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm transition hover:border-slate-300 hover:shadow-md sm:flex-row sm:items-stretch sm:gap-4",
        className
      )}
      data-testid={testId}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-2 flex items-start gap-3">
          {Icon && (
            <div
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-sm ring-1",
                iconWrap
              )}
            >
              <Icon className="h-5 w-5" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-slate-900">
                {title}
              </h3>
              {menu && <div className="shrink-0">{menu}</div>}
            </div>
            {description && (
              <p className="mt-1 line-clamp-2 text-xs text-slate-500">{description}</p>
            )}
            {badge && <div className="mt-2">{badge}</div>}
            {meta && <p className="mt-1 text-[11px] text-slate-400">{meta}</p>}
          </div>
        </div>
        {stats && <div className="mt-auto flex flex-wrap gap-4 pt-2">{stats}</div>}
      </div>
      {actions && (
        <div className="mt-3 flex shrink-0 flex-wrap items-end gap-2 border-t border-slate-100 pt-3 sm:mt-0 sm:flex-col sm:justify-end sm:border-0 sm:pt-0">
          {actions}
        </div>
      )}
    </div>
  );
}
